import { Add, KeyboardArrowDown } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { Divider } from "@mui/material";
import startsWith from "lodash.startswith";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import { useLocation } from "react-router-dom";
import { If } from "../../../../components/Condition";
import { countries } from "../../../../components/Countries";
import FlagById from "../../../../components/FlagById";
import LoadingModal from "../../../../components/LoadingModal/LoadingModal";
import { SuccessModal } from "../../../../components/SuccessModal";
import { BusinessSessionHelper } from "../../../../helpers/business-session.helper";
import { useAuthStore } from "../../../../store/session";
import { animateSelectStyle } from "../../../../styles/select";
import {
  CountryCode,
  PhNoFormat,
  PhNoWhiteListedCountries,
  WhiteListedCountries,
} from "../../../../utils/constants";
import { matchesFormat } from "../../../../utils/misc";
import { currencyList } from "../../../Order/EditOrders";
import { useContactFormContainer } from "./contactFormContainer";
import { UserCircleIcon } from "@heroicons/react/24/solid";

export default function ContactForm(props: {
  editMode?: boolean;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    errors,
    onSubmit,
    onSearching,
    searchContactByEmail,
    businessOptions,
    handleSetBusinessData,
    setError,
    success,
    loading,
    navigate,
    getValues,
    checkEmailOrPhone,
    trigger,
    clearErrors,
  } = useContactFormContainer({
    editMode: props.editMode,
    onSuccess: props.onSuccess,
  });
  const auth = useAuthStore();
  const { state } = useLocation();
  const isCreator = props.editMode
    ? state.contact?.createdBy?.id === auth.session?.account?.id
    : true;
  const isEmailDisabled =
    state != null && state.contact?.contactBusinessAccount != null;
  const isOwner = BusinessSessionHelper.IsOwner();
  const canEdit = isOwner || isCreator;
  const [searchContact, setSearchContact] = useState("");
  const [showSugest, setShowSugest] = useState(false);
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2} sx={{ padding: 2 }}>
          <FormControl error={!!errors.phoneOrEmail}>
            <FormLabel>Email or Phone Number</FormLabel>
            <Input
              {...register("phoneOrEmail")}
              type="text"
              placeholder={"Email or Phone"}
              sx={{ backgroundColor: "white" }}
              defaultValue={getValues("phoneOrEmail")}
              disabled={isEmailDisabled}
              onChange={(e) => {
                if (!props.editMode) {
                  searchContactByEmail(e.target.value);
                  if (checkEmailOrPhone(e.target.value) !== "OTHER") {
                    setSearchContact(e.target.value);
                    setShowSugest(true);
                  } else {
                    setShowSugest(false);
                  }
                }
              }}
            />
            {showSugest && !props.editMode && (
              <Sheet
                variant="outlined"
                color="neutral"
                sx={{
                  p: 2,
                  borderRadius: "0 0 8px 8px",
                  boxShadow: "0px 2px 3px 1px #00000026",
                  minHeight: "140px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {checkEmailOrPhone(searchContact) === "EMAIL" && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <p className="text-sm">
                        search email{" "}
                        <strong className="ml-2">“{searchContact}”</strong>
                      </p>
                      {businessOptions?.length > 0 ? (
                        <p className="text-sm italic">1 result</p>
                      ) : (
                        <p className="text-sm italic">no result</p>
                      )}
                    </Box>
                    {businessOptions?.length > 0 && (
                      <div
                        className="flex flex-row items-center gap-2 group relative p-3 rounded-md transition hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setShowSugest(false);
                        }}
                      >
                        <UserCircleIcon width={40} color="#98A2B3" />
                        <p className="text-sm font-bold">
                          {getValues("nameOfContact")}
                        </p>
                        <div className="ml-8">
                          <p className="font-bold text-sm mb-1">
                            {getValues("phoneOrEmail")}
                          </p>
                          <p className="text-sm">
                            +{getValues("phoneNumberCountryCode")}
                            {getValues("phoneNumber")}
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      className="flex flex-row text-[#499873]"
                      onClick={() => {
                        setShowSugest(false);
                      }}
                    >
                      <Add className="mr-2" />
                      <p className="text-sm">Add email as new contact</p>
                    </button>
                  </>
                )}
                {checkEmailOrPhone(searchContact) === "PHONE" && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <p className="text-sm">
                        search phone{" "}
                        <strong className="ml-2">“{searchContact}”</strong>
                      </p>
                      {businessOptions?.length > 0 ? (
                        <p className="text-sm italic">1 result</p>
                      ) : (
                        <p className="text-sm italic">no result</p>
                      )}
                    </Box>
                    {businessOptions?.length > 0 && (
                      <div
                        className="flex flex-row items-center gap-2 group relative p-3 rounded-md transition hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setShowSugest(false);
                        }}
                      >
                        <UserCircleIcon width={40} color="#98A2B3" />
                        <p className="text-sm font-bold">
                          {getValues("nameOfContact")}
                        </p>
                        <div className="ml-8">
                          {/* <p className="font-bold text-sm mb-1">{getValues("contactEmail")}</p> */}
                          <p className="text-sm">
                            +{getValues("phoneNumberCountryCode")}
                            {getValues("phoneNumber")}
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      className="flex flex-row text-[#499873]"
                      onClick={() => {
                        setShowSugest(false);
                        if (
                          checkEmailOrPhone(getValues("phoneOrEmail")) ===
                          "PHONE"
                        ) {
                          setValue(
                            "companyNumberCountryCode",
                            getValues("phoneOrEmail")?.slice(0, 2)
                          );
                          setValue(
                            "companyNumber",
                            getValues("phoneOrEmail").slice(2)
                          );
                        }
                      }}
                    >
                      <Add className="mr-2" />
                      <p className="text-sm">Add phone number as new contact</p>
                    </button>
                  </>
                )}
              </Sheet>
            )}
            {errors.phoneOrEmail?.message && (
              <FormHelperText>{errors.phoneOrEmail?.message}</FormHelperText>
            )}
            {onSearching && (
              <Typography>Searching contact to add...</Typography>
            )}
          </FormControl>

          <FormControl error={!!errors.companyName}>
            <FormLabel>{t("profile.business.companyName")}</FormLabel>
            {businessOptions?.length > 1 ? (
              <Select
                placeholder={t("profile.business.companyName")}
                indicator={<KeyboardArrowDown />}
                onChange={(_, value) => {
                  if (value)
                    handleSetBusinessData(
                      businessOptions.find((b: any) => b.id === value)
                    );
                }}
                renderValue={(option: any) => (
                  <Typography>{option?.label}</Typography>
                )}
                sx={{ ...animateSelectStyle, backgroundColor: "white" }}
              >
                {businessOptions.map((business: any) => (
                  <Option
                    key={business.id}
                    value={business.id}
                    label={business.companyName}
                  >
                    <Typography>{business.companyName}</Typography>
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder={t("profile.business.companyName")}
                disabled={!canEdit}
                sx={{ backgroundColor: "white" }}
                {...register("companyName")}
              />
            )}
            {errors.companyName?.message && (
              <FormHelperText>{errors.companyName?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl error={!!errors.nameOfContact}>
            <FormLabel>{t("profile.business.contactName")}</FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.contactName")}
              sx={{ backgroundColor: "white" }}
              {...register("nameOfContact")}
            />
            {errors.nameOfContact?.message && (
              <FormHelperText>{errors.nameOfContact?.message}</FormHelperText>
            )}
          </FormControl>
          <Box
            flexDirection={{
              xs: "column",
              md: "row",
            }}
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <FormControl error={!!errors.companyCountry}>
              <FormLabel>{t("profile.business.country")}</FormLabel>

              <Select
                disabled={!canEdit}
                placeholder={t("profile.business.country")}
                indicator={<KeyboardArrowDown />}
                onChange={(_, value) => {
                  if (value) setValue("companyCountry", value);
                }}
                renderValue={(option) => (
                  <Typography
                    startDecorator={<FlagById id={`${option?.value ?? ""}`} />}
                  >
                    {option?.label}
                  </Typography>
                )}
                value={watch("companyCountry")}
                sx={{ ...animateSelectStyle, backgroundColor: "white" }}
              >
                {countries
                  .filter((country) =>
                    WhiteListedCountries.includes(country.code)
                  )
                  .map((country) => (
                    <Option
                      key={country.code}
                      value={country.code}
                      label={country.label}
                    >
                      <Typography
                        startDecorator={<FlagById id={country.code} />}
                      >
                        {country.label}
                      </Typography>
                    </Option>
                  ))}
              </Select>
              {errors.companyCountry?.message && (
                <FormHelperText>
                  {errors.companyCountry?.message}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ flex: "1" }} error={!!errors.companyNumber}>
              <FormLabel>
                {t("profile.business.companyNumber")}
              </FormLabel>
              <PhoneInput
                disabled={!canEdit}
                value={
                  (watch("companyNumber") &&
                    `+${watch("companyNumberCountryCode")}${watch(
                      "companyNumber"
                    )}`) ||
                  ""
                }
                onlyCountries={PhNoWhiteListedCountries}
                enableTerritories={false}
                masks={{
                  my: "..-....-....",
                  sg: "....-....",
                  th: "... ... ...",
                  id: "... ... ... ...",
                  mm: ". .... .... .....",
                  au: "... ... ...",
                }}
                country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                isValid={(inputNumber, meta: any, countries) => {
                  if (inputNumber === "" || inputNumber === meta.dialCode) {
                    return true;
                  }

                  if (inputNumber.toString().startsWith(`${meta.dialCode}0`)) {
                    if (!errors.companyNumber?.message) {
                      setError("companyNumber", {
                        message: t("commons.error.form.mobileInvalidStartZero"),
                      });
                    }
                    return false;
                  }

                  const result = (countries || []).some((country: any) => {
                    if (
                      startsWith(inputNumber, country.dialCode) ||
                      startsWith(country.dialCode, inputNumber)
                    ) {
                      const iso2: CountryCode = meta.iso2 as CountryCode;
                      const format = PhNoFormat[iso2];
                      if (!format) return false;
                      return matchesFormat(
                        inputNumber,
                        format,
                        country.countryCode
                      );
                    }
                    return false;
                  });

                  if (!result && !errors.companyNumber?.message) {
                    setError("companyNumber", {
                      message: t("commons.error.form.mobileInvalid"),
                    });
                  } else if (result && !!errors.companyNumber?.message) {
                    if (errors.companyNumber.type === "manual") {
                      return false;
                    }
                    clearErrors("companyNumber");
                    trigger("companyNumber");
                  }
                  return result;
                }}
                inputStyle={{
                  height: "50px",
                  width: "100%",
                  fontSize: "16px",
                }}
                onChange={(phone, meta: any) => {
                  setValue("companyNumberCountryCode", meta.dialCode);
                  setValue(
                    "companyNumber",
                    phone.replace(new RegExp(meta.dialCode), "")
                  );
                }}
              />
              {errors.companyNumber?.message && (
                <FormHelperText>{errors.companyNumber?.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
          <FormControl sx={{ flex: 3 }} error={!!errors.companyAddress}>
            <FormLabel>{t("profile.business.address")}</FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.address")}
              sx={{ backgroundColor: "white" }}
              {...register("companyAddress")}
            />
            {errors.companyAddress?.message && (
              <FormHelperText>{errors.companyAddress?.message}</FormHelperText>
            )}
          </FormControl>
          <Box
            flexDirection={{
              xs: "column",
              md: "row",
            }}
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <FormControl sx={{ flex: 1 }} error={!!errors.companyProvince}>
              <FormLabel>{t("profile.business.province")}</FormLabel>
              <Input
                disabled={!canEdit}
                placeholder={t("profile.business.province")}
                sx={{ backgroundColor: "white" }}
                {...register("companyProvince")}
              />
              {errors.companyProvince?.message && (
                <FormHelperText>
                  {errors.companyProvince?.message}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.companyPostalCode}>
              <FormLabel>{t("profile.business.postalCode")}</FormLabel>
              <Input
                disabled={!canEdit}
                placeholder={t("profile.business.postalCode")}
                sx={{ backgroundColor: "white" }}
                {...register("companyPostalCode")}
              />
              {errors.companyPostalCode?.message && (
                <FormHelperText>
                  {errors.companyPostalCode?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <FormControl sx={{ flex: 1 }} error={!!errors.companyPaymentTerms}>
            <FormLabel>{t("profile.business.paymentTerms")}</FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.paymentTerms")}
              sx={{ backgroundColor: "white" }}
              {...register("companyPaymentTerms")}
            />
            {errors.companyPaymentTerms?.message && (
              <FormHelperText>
                {errors.companyPaymentTerms?.message}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            sx={{ flex: 1 }}
            error={
              !!errors.companyCreditLimit || !!errors.companyCreditLimitCurrency
            }
          >
            <FormLabel>{t("profile.business.companyCreditLimit")}</FormLabel>
            <Input
              disabled={!canEdit}
              type={"number"}
              placeholder={t("profile.business.companyCreditLimit")}
              sx={{ backgroundColor: "white" }}
              defaultValue={0}
              value={watch("companyCreditLimit") ?? 0}
              onChange={(event) => {
                setValue(
                  "companyCreditLimit",
                  parseInt(event.target.value) ?? 0
                );
              }}
              startDecorator={
                <React.Fragment>
                  <Select
                    disabled={!canEdit}
                    variant="plain"
                    defaultValue={""}
                    value={watch("companyCreditLimitCurrency") ?? ""}
                    indicator={<KeyboardArrowDown />}
                    onChange={(_, value) =>
                      setValue("companyCreditLimitCurrency", value ?? "IDR")
                    }
                    sx={{ ...animateSelectStyle, paddingLeft: 0 }}
                  >
                    {currencyList.map((c) => {
                      return (
                        <Option id={c} value={c}>
                          {c}
                        </Option>
                      );
                    })}
                  </Select>
                  <Divider orientation="vertical" />
                </React.Fragment>
              }
            />
            {(errors.companyCreditLimit?.message ||
              errors.companyCreditLimitCurrency?.message) && (
              <FormHelperText>
                {errors.companyCreditLimit?.message ||
                  errors.companyCreditLimitCurrency?.message}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl sx={{ flex: 1 }} error={!!errors.companyBankName}>
            <FormLabel>{t("profile.business.companyBankName")}</FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.companyBankName")}
              sx={{ backgroundColor: "white" }}
              {...register("companyBankName")}
            />
            {errors.companyBankName?.message && (
              <FormHelperText>{errors.companyBankName?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl sx={{ flex: 1 }} error={!!errors.companyBankAccountNo}>
            <FormLabel>{t("profile.business.companyBankAccountNo")}</FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.companyBankAccountNo")}
              sx={{ backgroundColor: "white" }}
              {...register("companyBankAccountNo")}
            />
            {errors.companyBankAccountNo?.message && (
              <FormHelperText>
                {errors.companyBankAccountNo?.message}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            disabled={!canEdit}
            sx={{ flex: 1 }}
            error={!!errors.companyBankAccountHolderName}
          >
            <FormLabel>
              {t("profile.business.companyBankAccountHolderName")}
            </FormLabel>
            <Input
              disabled={!canEdit}
              placeholder={t("profile.business.companyBankAccountHolderName")}
              sx={{ backgroundColor: "white" }}
              {...register("companyBankAccountHolderName")}
            />
            {errors.companyBankAccountHolderName?.message && (
              <FormHelperText>
                {errors.companyBankAccountHolderName?.message}
              </FormHelperText>
            )}
          </FormControl>
          <br />
          <If condition={canEdit}>
            <Box
              flexDirection={{
                xs: "column",
                md: "row",
              }}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="plain"
                sx={{
                  fontSize: "16px",
                }}
                onClick={() =>
                  props.onCancel
                    ? props.onCancel()
                    : navigate("/client/business-contact")
                }
              >
                {t("commons.cancelBtn")}
              </Button>
              <Button
                type="submit"
                sx={{
                  fontSize: "16px",
                }}
              >
                {t("manage.businessContact.saveContactBtn")}
              </Button>
            </Box>
          </If>
        </Stack>
      </form>
      <LoadingModal isLoading={loading} />
      <SuccessModal
        open={success}
        title="Success"
        content="Your contact has been saved successfully."
      />
    </>
  );
}
